import React from "react";
import { useSelector } from "react-redux";
import { Hex } from "viem";
import { useEmbeddedReveal } from "@dynamic-labs/sdk-react-core";
import { KeyIcon, TicketIcon } from "lucide-react";
import isEmpty from "lodash.isempty";

import UsdcStrokeIcon from "@/assets/svg/usdc-stroke.svg?react";
import {
  AvatarBadge,
  CopyToClipboardButton,
  WithTooltip,
} from "@/components/main";
import { Button, Separator, Skeleton } from "@/components/ui";
import { WalletType } from "@/types";
import { formatBalance, formatCurrency } from "@/helpers";
import { RootState } from "@/store";
import { TicketSummary } from "@/pages/profile/components/profileUtils";
import { useTranslate } from "@/hooks";
import { cn } from "@/helpers/tailwind";

interface WalletQuickInfoProps {
  className?: string;
  balance: string;
  walletAddress: Hex | null;
  walletType: WalletType;
  ticketSummary: TicketSummary[];
  isLoading: boolean;
  hideProfileSummary?: boolean;
}

export const WalletQuickInfo = ({
  className,
  balance,
  walletAddress,
  walletType,
  ticketSummary,
  isLoading,
  hideProfileSummary,
}: WalletQuickInfoProps) => {
  const t = useTranslate();
  const { initExportProcess } = useEmbeddedReveal();

  return (
    <div className={cn(className)}>
      <div
        className={cn(
          "mb-1 rounded-b-sm rounded-t-3xl bg-indigo-400/10 p-4 md:p-6",
          "flex flex-col items-center gap-2 sm:flex-row",
          {
            hidden: hideProfileSummary,
          }
        )}>
        <div className="mb-2 flex w-full flex-grow flex-row-reverse items-center justify-between gap-2 sm:mb-0 sm:w-auto sm:flex-row">
          <AvatarBadge className="mr-2 size-8 lg:size-10" />
          <p className="flex-grow text-sm">Profile</p>
        </div>
        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end">
          <CopyToClipboardButton
            className="mr-2 text-xs sm:text-base"
            copyText={walletAddress ?? ""}
            displayText={`${walletAddress?.slice(0, 4)}...${walletAddress?.slice(-4)}`}
          />
          {walletType === WalletType.SMART_ACCOUNT && (
            <Button
              className="md:text-md flex h-min items-center justify-center rounded-full border border-indigo-400 bg-transparent px-3 py-1 text-xs text-indigo-400 hover:border-indigo-400/80 hover:bg-transparent"
              onClick={() => initExportProcess()}>
              <span>{t("components.export_private_key")}</span>
              <KeyIcon />
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <div
          className={cn(
            "rounded-sm rounded-bl-3xl bg-indigo-400/10 p-4 sm:p-6",
            { "rounded-l-3xl": hideProfileSummary }
          )}>
          <div className="mb-2 flex flex-row-reverse items-center justify-between sm:mb-1 sm:flex-col sm:items-start">
            <div className="mb-0 flex size-9 items-center justify-center gap-2 rounded-full bg-indigo-400/10 sm:mb-4">
              <UsdcStrokeIcon className="size-4.5" />
            </div>
            <p className="text-xs sm:text-sm">Balance</p>
          </div>
          <p className="whitespace-nowrap">
            <span className="mr-1 text-lg sm:text-xl">$</span>
            <span className="font-phonk text-xl sm:text-2xl">
              <WithTooltip
                tooltipContent={`$${formatCurrency(Number(balance))}`}>
                {formatBalance(Number(balance))}
              </WithTooltip>
            </span>
          </p>
        </div>
        <div
          className={cn(
            "rounded-sm rounded-br-3xl bg-indigo-400/10 p-4 sm:p-6",
            { "rounded-r-3xl": hideProfileSummary }
          )}>
          <div className="mb-2 flex flex-row-reverse items-center justify-between sm:mb-1 sm:flex-col sm:items-start">
            <div className="mb-0 flex size-9 items-center justify-center gap-2 rounded-full bg-indigo-400/10 sm:mb-4">
              <TicketIcon className="size-5" />
            </div>
            <p className="text-xs sm:text-sm">Tickets</p>
          </div>
          {isLoading && <Skeleton className="h-8 w-full bg-indigo-400/20" />}
          {!isLoading && isEmpty(ticketSummary) && (
            <div className="flex items-end gap-2">
              <span className="flex size-7 items-center justify-center rounded-full border border-indigo-400/30 text-xs text-indigo-300">
                0x
              </span>
              <p>
                <span className="mr-1 text-lg sm:text-xl">$</span>
                <span className="font-phonk text-xl sm:text-2xl">-</span>
              </p>
            </div>
          )}
          {!isLoading && !isEmpty(ticketSummary) && (
            <div className="flex items-center gap-4">
              {ticketSummary.map(({ amount, count }, index) => (
                <React.Fragment key={amount}>
                  {index === 1 && (
                    <Separator
                      orientation="vertical"
                      className="h-6 bg-indigo-300"
                    />
                  )}
                  <div className="flex items-end gap-2">
                    <span className="flex size-7 items-center justify-center rounded-full border border-indigo-400/30 text-xs text-indigo-300">
                      {count}x
                    </span>
                    <p>
                      <span className="mr-1 text-lg sm:text-xl">$</span>
                      <span className="font-phonk text-xl sm:text-2xl">
                        {amount}
                      </span>
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
